import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  Template,
  TemplateRequest,
  TemplateFilters,
  TemplateResult,
  UpdateTemplatePayload,
} from "../../schemas/templateSchema";

const GET_TEMPLATES = gql`
  query GetTemplates(
    $page: Int
    $pageSize: Int
    $search: String
    $categoryId: ID
    $type: String
  ) {
    templates(
      page: $page
      pageSize: $pageSize
      search: $search
      categoryId: $categoryId
      type: $type
    ) {
      templates {
        id
        name
        type
        description
        categoryId
        category {
          id
          name
        }
        zipUrl
        previewUrl
        tenantId
        createdBy
        createdAt
        updatedAt
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

const GET_TEMPLATE_BY_ID = gql`
  query GetTemplateById($id: ID!) {
    template(id: $id) {
      id
      name
      type
      description
      categoryId
      category {
        id
        name
      }
      zipUrl
      previewUrl
      tenantId
      createdBy
      createdAt
      updatedAt
    }
  }
`;

const CREATE_TEMPLATE = gql`
  mutation CreateTemplate($input: CreateTemplateInput!) {
    createTemplate(input: $input)
  }
`;

const UPDATE_TEMPLATE = gql`
  mutation UpdateTemplate($id: ID!, $input: UpdateTemplateInput!) {
    updateTemplate(id: $id, input: $input)
  }
`;

const DELETE_TEMPLATES = gql`
  mutation DeleteTemplates($ids: [ID!]!) {
    deleteTemplates(ids: $ids)
  }
`;

export const templateService = {
  getAll: async (filters: TemplateFilters = {}): Promise<TemplateResult> => {
    const { data } = await client.query({
      query: GET_TEMPLATES,
      variables: filters,
      fetchPolicy: "network-only",
    });
    return data.templates;
  },

  getById: async (id: string): Promise<Template> => {
    const { data } = await client.query({
      query: GET_TEMPLATE_BY_ID,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data.template;
  },

  create: async (input: TemplateRequest): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_TEMPLATE,
      variables: { input },
      context: {
        headers: {
          "Apollo-Require-Preflight": "true",
        },
      },
    });
    return data.createTemplate;
  },

  update: async (payload: UpdateTemplatePayload): Promise<boolean> => {
    const input: Record<string, unknown> = {};
    if (payload.template.name !== undefined) input.name = payload.template.name;
    if (payload.template.type !== undefined) input.type = payload.template.type;
    if (payload.template.description !== undefined)
      input.description = payload.template.description;
    if (payload.template.categoryId !== undefined)
      input.categoryId = payload.template.categoryId;
    if (payload.template.zipFile && payload.template.zipFile.length > 0)
      input.zipFile = payload.template.zipFile[0];
    if (payload.template.previewFile && payload.template.previewFile.length > 0)
      input.previewFile = payload.template.previewFile[0];

    const { data } = await client.mutate({
      mutation: UPDATE_TEMPLATE,
      variables: {
        id: payload.id,
        input,
      },
      context:
        input.zipFile || input.previewFile
          ? {
              headers: {
                "Apollo-Require-Preflight": "true",
              },
            }
          : undefined,
    });
    return data.updateTemplate;
  },

  remove: async (ids: string[]): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_TEMPLATES,
      variables: { ids },
    });
    return data.deleteTemplates;
  },
};
